import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../../styles/lp-variables.css';

/**
 * DiagnosePage - URL input form for service analysis
 * Step 1 of the onboarding flow: User enters their service URL.
 * On submit, navigates to /analyzing with the URL as a query parameter.
 */
const DiagnosePage: React.FC = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const validateUrl = (value: string): boolean => {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!url.trim()) {
      setError('URLを入力してください');
      return;
    }

    if (!validateUrl(url)) {
      setError('URLの形式が正しくありません');
      return;
    }

    setSubmitting(true);

    // Short delay for UX feedback before navigation
    await new Promise((r) => setTimeout(r, 600));
    navigate(`/analyzing?url=${encodeURIComponent(url)}`);
  };

  return (
    <div
      className="landing-page"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%', maxWidth: '512px' }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h2
            style={{
              fontSize: '28px',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'var(--lp-text-primary)',
              lineHeight: 1.4,
            }}
          >
            あなたの商品を分析し、
            <br />
            戦略レポートとバナーを作成します。
          </h2>
          <p
            style={{
              marginTop: '12px',
              fontSize: '16px',
              color: 'var(--lp-text-secondary)',
              lineHeight: 1.7,
            }}
          >
            URLを入力するだけ。AIがサービスを分析し、
            <br />
            最適な広告戦略レポートとバナーを自動生成します。
          </p>
        </div>

        {/* URL Input */}
        <div>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--lp-text-primary)',
              marginBottom: '6px',
            }}
          >
            分析するサービスのURL
          </label>
          <input
            type="url"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              if (error) setError('');
            }}
            style={{
              width: '100%',
              height: '56px',
              padding: '0 20px',
              borderRadius: '12px',
              border: `1px solid ${error ? '#f87171' : 'var(--lp-border-strong)'}`,
              backgroundColor: '#fff',
              fontSize: '16px',
              color: 'var(--lp-text-primary)',
              outline: 'none',
              transition: 'border-color 0.2s',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => {
              if (!error) {
                e.target.style.borderColor = 'var(--lp-accent-green)';
              }
            }}
            onBlur={(e) => {
              if (!error) {
                e.target.style.borderColor = 'var(--lp-border-strong)';
              }
            }}
          />
          {error && (
            <p style={{ marginTop: '6px', fontSize: '13px', color: '#ef4444' }}>
              {error}
            </p>
          )}
          <p
            style={{
              marginTop: '6px',
              fontSize: '13px',
              color: 'var(--lp-text-tertiary)',
            }}
          >
            分析したいLP・サービスサイトのURLを入力してください
          </p>
        </div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={submitting}
          whileTap={{ scale: 0.98 }}
          style={{
            marginTop: '24px',
            width: '100%',
            height: '56px',
            borderRadius: '9999px',
            backgroundColor: 'var(--lp-accent-green)',
            color: '#fff',
            fontSize: '17px',
            fontWeight: 600,
            border: 'none',
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.6 : 1,
            transition: 'opacity 0.2s',
          }}
        >
          {submitting ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <svg
                style={{ animation: 'spin 1s linear infinite', height: '20px', width: '20px' }}
                viewBox="0 0 24 24"
              >
                <circle
                  style={{ opacity: 0.25 }}
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  style={{ opacity: 0.75 }}
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              分析を開始しています...
            </span>
          ) : (
            '無料で分析する'
          )}
        </motion.button>
      </motion.form>

      {/* Spinner animation keyframes */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DiagnosePage;
